import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, FileText, Printer, ArrowLeft } from 'lucide-react';
import { prescriptionService } from '../../consultation/api/prescriptions';
import { encounterService } from '../../consultation/api/encounters';
import { patientService, doctorService } from '../../../lib/services';
import { useStore } from '../../../lib/store';
import { getDoctorIdFromUser, isDoctor } from '../../../lib/rbac';
import { printPrescription } from '../../consultation/utils/prescriptionPdf';
import toast from 'react-hot-toast';
import type { Prescription, Encounter } from '../../consultation/types';
import type { Patient, Doctor } from '../../../lib/supabase';

const PrescriptionView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useStore();
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [encounter, setEncounter] = useState<Encounter | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [loading, setLoading] = useState(true);

  const doctorId = getDoctorIdFromUser(user);

  useEffect(() => {
    if (!isDoctor(user)) {
      toast.error('Access denied. Doctor privileges required.');
      return;
    }
    
    if (!doctorId) {
      toast.error('Doctor ID not found. Please contact administrator.');
      return;
    }

    if (id) {
      loadData();
    }
  }, [id, user, doctorId]);

  const loadData = async () => {
    if (!id || !doctorId) return;
    
    try {
      setLoading(true);
      const prescriptionData = await prescriptionService.getById(id);
      
      const encounterData = await encounterService.getById(prescriptionData.encounterId);
      
      if (encounterData.doctorId !== doctorId) {
        toast.error('Access denied. You can only view your own prescriptions.');
        return;
      }

      setPrescription(prescriptionData);
      setEncounter(encounterData);

      const [patientData, doctorData] = await Promise.all([
        patientService.getById(encounterData.patientId),
        doctorService.getById(encounterData.doctorId),
      ]);

      setPatient(patientData);
      setDoctor(doctorData);
    } catch (error) {
      console.error('Failed to load prescription data:', error);
      toast.error('Failed to load prescription');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPrescription = () => {
    if (prescription && encounter && patient && doctor) {
      const encounterWithPatient = {
        ...encounter,
        patient,
        doctor,
      };
      printPrescription(prescription, encounterWithPatient);
    }
  };

  if (!isDoctor(user)) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500">Access denied. Doctor privileges required.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!prescription || !encounter) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Prescription not found</h2>
        <Link to="/doctor/prescriptions" className="text-blue-600 hover:text-blue-800 mt-2 inline-block">
          Back to Prescriptions
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/doctor/prescriptions"
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Prescription Details</h1>
            <p className="text-gray-600">
              Created on {prescription.createdAt ? new Date(prescription.createdAt).toLocaleDateString() : 'Unknown date'}
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handlePrintPrescription}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Printer className="w-4 h-4" />
            <span>Print Prescription</span>
          </button>
          <Link
            to={`/prescriptions/${prescription.id}/print`}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <FileText className="w-4 h-4" />
            <span>Print View</span>
          </Link>
        </div>
      </div>

      {patient && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <User className="w-5 h-5 mr-2" />
            Patient Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Name</p>
              <p className="text-sm text-gray-900">{patient.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Email</p>
              <p className="text-sm text-gray-900">{patient.email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Phone</p>
              <p className="text-sm text-gray-900">{patient.phone || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Date of Birth</p>
              <p className="text-sm text-gray-900">
                {patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}

      {doctor && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Prescribing Doctor</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Name</p>
              <p className="text-sm text-gray-900">{doctor.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Specialization</p>
              <p className="text-sm text-gray-900">{doctor.specialization || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">License Number</p>
              <p className="text-sm text-gray-900">{doctor.license_number || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Department</p>
              <p className="text-sm text-gray-900">{doctor.department || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Prescription Details
        </h3>
        
        <div className="space-y-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Medications</h4>
            <div className="space-y-3">
              {prescription.items.map((item, index) => (
                <div key={index} className="border-l-4 border-blue-200 pl-4 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <h5 className="font-medium text-gray-900">{item.nameOrMedicineId}</h5>
                    <span className="text-sm text-gray-500 capitalize">{item.route}</span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="font-medium">Dose:</span> {item.dose}
                      </div>
                      <div>
                        <span className="font-medium">Frequency:</span> {item.frequency}
                      </div>
                      <div>
                        <span className="font-medium">Duration:</span> {item.duration}
                      </div>
                    </div>
                    {item.instructions && (
                      <div className="mt-2">
                        <span className="font-medium">Instructions:</span> 
                        <span className="italic ml-1">{item.instructions}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {prescription.advice && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Clinical Advice</h4>
              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                {prescription.advice}
              </p>
            </div>
          )}

          {prescription.followUpDate && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Follow-up Date</h4>
              <p className="text-sm text-gray-700">
                {new Date(prescription.followUpDate).toLocaleDateString()}
              </p>
            </div>
          )}

          {prescription.signedBy && (
            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Signed by:</span> {prescription.signedBy}
              </p>
              <p className="text-sm text-gray-500">
                Digital signature on {prescription.createdAt ? new Date(prescription.createdAt).toLocaleString() : 'Unknown date'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionView;
